import { useEffect, useRef } from "react";
import { useRequestMutationMultiple } from "hooks/useRequest";
import { BinderItem, CollectionAdditionalInfo } from "models/BinderItem";
import { useDispatch } from "react-redux";
import {
  changeIsLoading,
  changeIsAdding,
  setPersonalTotalCount,
  setNikkeiTotalCount,
  setPersonalBinderItems,
  setNikkeiBinderItems,
  setHomeBinderItems
} from "modules/binders/list/actions";
import { useReduxState } from "hooks/useReduxState";
import { isEqual } from "utils/helper/common";
import { useToast } from "hooks/useToast";

interface FetchBaseCollectionParams {
  needFeedArticles: number;
  offset?: number;
  limit: number;
  sortKbn: string;
  keyword: string;
}

interface State {
  totalCount: "personalTotalCount" | "nikkeiTotalCount";
}

// ストアの取得先定義
const storeState: {
  personal: State;
  nikkei: State;
  home: State;
} = {
  personal: {
    totalCount: "personalTotalCount"
  },
  nikkei: {
    totalCount: "nikkeiTotalCount"
  },
  home: {
    totalCount: "personalTotalCount"
  }
} as const;

// アクション定義インターフェース
interface Actions {
  // コレクション設定
  setCollection: (items: BinderItem[]) => void;
  // 総件数設定
  setTotalCount: (count: number) => void;
}

// アクション定義
const actions: {
  personal: Actions;
  nikkei: Actions;
  home: Omit<Actions, "setTotalCount">;
} = {
  personal: {
    setCollection: setPersonalBinderItems,
    setTotalCount: setPersonalTotalCount
  },
  nikkei: {
    setCollection: setNikkeiBinderItems,
    setTotalCount: setNikkeiTotalCount
  },
  home: {
    setCollection: setHomeBinderItems
  }
} as const;

// 除外フィールド定数（APIに送信する値）
const EXCLUDE_FIELDS = {
  LIST_COUNT: "1", // リスト件数
  ARTICLE: "2", // 記事
  CORP_LIST: "3", // 企業リスト
  FEED_CONDITION: "4" // フィード条件
} as const;

// フラグの有効値
const ENABLED_FLAG = "1";

/**
 * コレクションの取得と追加情報のマージを行うカスタムフック
 * 取得したコレクションはストアに保存する（コレクションの取得をアクションモーダルなどから呼び出されるため）
 */
export const useMergeCollection = (
  fetchBaseCollection: (
    params: FetchBaseCollectionParams
  ) => Promise<{
    binderList: BinderItem[];
    totalCount: number;
  }>,
  type: "personal" | "nikkei" | "home"
) => {
  const {
    data,
    trigger: fetchAdditionalCollectionInfo,
    clearData
  } = useRequestMutationMultiple<CollectionAdditionalInfo>(
    "/binder/contents/binder-list",
    "GET"
  );

  // コレクション
  const baseCollection = useReduxState(state => state.binders.list[type]);
  // 総件数
  const totalCount = useReduxState(
    state => state.binders.list[storeState[type].totalCount]
  );
  // ローディング状態フラグ
  const isLoading = useReduxState(state => state.binders.list.isLoading);
  // コレクション追加中フラグ
  const isAdding = useReduxState(state => state.binders.list.isAdding);

  const prevAdditionalInfo = useRef<CollectionAdditionalInfo[] | null>(null);
  const baseCollectionRef = useRef<BinderItem[]>(baseCollection);

  const toast = useToast();

  const dispatch = useDispatch();

  // コレクションのRefを更新
  useEffect(() => {
    baseCollectionRef.current = baseCollection;
  }, [baseCollection]);

  const clearCollectionData = () => {
    // 追加情報のデータクリア
    clearData();
    // コレクションをストアに保存
    dispatch(actions[type].setCollection([]));
    // コレクション連続取得時に古いコレクションの参照を避ける
    baseCollectionRef.current = [];

    // 総件数をストアに保存（ホームは総件数がないためスキップ）
    if (type !== "home") {
      dispatch(actions[type].setTotalCount(0));
    }
  };

  // コレクション情報更新
  const fetchCollections = async (params: FetchBaseCollectionParams) => {
    try {
      // ローディング状態フラグON
      dispatch(changeIsLoading(true));

      // コレクション情報のクリア
      clearCollectionData();

      // コレクションを取得
      const baseCollectionResponse = await fetchBaseCollection(params);
      const baseBinderList = baseCollectionResponse.binderList;

      // コレクションをストアに保存
      dispatch(actions[type].setCollection(baseBinderList));
      // 追加情報のマージ時に最新のリストを参照できるよう更新
      baseCollectionRef.current = baseBinderList;

      // 総件数をストアに保存（ホームは総件数がないためスキップ）
      if (type !== "home") {
        dispatch(
          actions[type].setTotalCount(baseCollectionResponse.totalCount)
        );
      }

      // ローディング状態フラグOFF
      dispatch(changeIsLoading(false));

      // 追加で情報を取得するコレクションを絞り込む
      const additionCollection = baseBinderList.filter(
        item =>
          item.listCountGetFlg !== ENABLED_FLAG ||
          item.articleGetFlg !== ENABLED_FLAG ||
          item.corpListGetFlg !== ENABLED_FLAG ||
          item.feedConditionGetFlg !== ENABLED_FLAG
      );

      // 追加情報を取得
      fetchAdditionalCollectionInfo(
        additionCollection.map(item => {
          const excludeFields = [];

          if (item.listCountGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.LIST_COUNT);
          if (item.articleGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.ARTICLE);
          if (item.corpListGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.CORP_LIST);
          if (item.feedConditionGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.FEED_CONDITION);

          return {
            binderIds: item.binderInfo.binderId,
            excludeFields: excludeFields.join(",")
          };
        })
      );
    } catch (error) {
      if ((error as any).message === "GATEWAY_TIMEOUT") {
        toast.setErrors(
          "タイムアウトしました。ページを更新・また再度検索してください。"
        );
      }
      dispatch(changeIsLoading(false));
    }
  };

  // 追加でコレクションを取得
  const addCollection = async (params: FetchBaseCollectionParams) => {
    try {
      // 追加コレクションの取得中フラグON
      dispatch(changeIsAdding(true));

      // コレクションを取得
      const baseCollectionResponse = await fetchBaseCollection(params);
      const newBaseBinderList = baseCollectionResponse.binderList;

      const mergedCollection = [
        ...baseCollectionRef.current,
        ...newBaseBinderList
      ];
      // 追加コレクションをストアに保存
      dispatch(actions[type].setCollection(mergedCollection));
      // コレクションの連続取得時でも最新のコレクションを参照できるよう更新
      baseCollectionRef.current = mergedCollection;

      // 総件数をストアに保存（ホームは総件数がないためスキップ）
      if (type !== "home") {
        dispatch(
          actions[type].setTotalCount(baseCollectionResponse.totalCount)
        );
      }

      // 追加コレクションの取得中フラグOFF
      dispatch(changeIsAdding(false));

      // 追加で情報を取得するコレクションを絞り込む
      const additionCollection = newBaseBinderList.filter(
        item =>
          item.listCountGetFlg !== ENABLED_FLAG ||
          item.articleGetFlg !== ENABLED_FLAG ||
          item.corpListGetFlg !== ENABLED_FLAG ||
          item.feedConditionGetFlg !== ENABLED_FLAG
      );

      // 追加情報を取得
      fetchAdditionalCollectionInfo(
        additionCollection.map(item => {
          const excludeFields = [];

          if (item.listCountGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.LIST_COUNT);
          if (item.articleGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.ARTICLE);
          if (item.corpListGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.CORP_LIST);
          if (item.feedConditionGetFlg === ENABLED_FLAG)
            excludeFields.push(EXCLUDE_FIELDS.FEED_CONDITION);

          return {
            binderIds: item.binderInfo.binderId,
            excludeFields: excludeFields.join(",")
          };
        })
      );
    } catch (error) {
      if ((error as any).message === "GATEWAY_TIMEOUT") {
        toast.setErrors(
          "タイムアウトしました。ページを更新・また再度検索してください。"
        );
      }
      dispatch(changeIsAdding(false));
    }
  };

  useEffect(() => {
    // データが変更されていない場合は早期リターン
    // パフォーマンスのため、まず長さを比較し、長さが同じ場合のみ詳細な比較を行う
    if (
      prevAdditionalInfo.current !== null &&
      prevAdditionalInfo.current.length === data.length &&
      isEqual(prevAdditionalInfo.current, data)
    ) {
      return;
    }

    // コレクションと追加情報をマージ
    const updatedCollections: BinderItem[] = baseCollectionRef.current.map(
      baseItem => {
        const addInfo = data.find(
          add =>
            add?.binderList?.[0].binderInfo.binderId ===
            baseItem.binderInfo.binderId
        );

        if (addInfo) {
          const {
            binderInfo,
            corpTotalCount,
            industryTotalCount,
            statisticsTotalCount,
            saveArticleTotalCount,
            externalPageTotalCount,
            articleList
          } = addInfo.binderList[0];

          const collectionInfo: BinderItem = {
            ...baseItem,
            ...(baseItem.listCountGetFlg !== ENABLED_FLAG && {
              corpTotalCount: corpTotalCount || "0",
              industryTotalCount: industryTotalCount || "0",
              statisticsTotalCount: statisticsTotalCount || "0",
              saveArticleTotalCount: saveArticleTotalCount || "0",
              externalPageTotalCount: externalPageTotalCount || "0"
            }),
            ...(baseItem.articleGetFlg !== ENABLED_FLAG && {
              articleList: articleList || []
            }),
            binderInfo: {
              ...baseItem.binderInfo,
              ...(baseItem.corpListGetFlg !== ENABLED_FLAG && {
                corpList: binderInfo.corpList || []
              }),
              ...(baseItem.feedConditionGetFlg !== ENABLED_FLAG && {
                mediaList: binderInfo.mediaList || [],
                query: binderInfo.query || "",
                includeRelatedCorp: binderInfo.includeRelatedCorp || "0"
              })
            },
            // 追加情報を取得したことを示すフラグ
            articleGetFlg: ENABLED_FLAG,
            listCountGetFlg: ENABLED_FLAG,
            feedConditionGetFlg: ENABLED_FLAG,
            corpListGetFlg: ENABLED_FLAG
          };

          return collectionInfo;
        }
        return baseItem;
      }
    );

    dispatch(actions[type].setCollection(updatedCollections));
    prevAdditionalInfo.current = [...data];
  }, [data, dispatch, type]);

  return {
    collections: baseCollection,
    totalCount,
    isLoading,
    isAdding,
    fetchCollections,
    addCollection
  };
};
